import { type Raycaster, type OrthographicCamera, Vector2, type Object3D } from 'three';
import { watch } from 'vue';
import { stopAll, type StopHandle } from '@/util/stopHandle';
import type { HipSpineXraySceneContext } from '@/views/3d-planner/hipSpine/scene/hipSpineXrayScene';
import type { useHipSpineXRayEditViewModel } from '@/views/3d-planner/hipSpine/xrayEdit/useHipSpineXRayEditViewModel';
import type { useHipSpineStore } from '@/views/3d-planner/hipSpine/hipSpineStore';
import { isPointOnCircleEdge } from '@/views/3d-planner/hipSpine/interaction/landmarkHitTesting';
import { INTERACTION_CURSORS } from '@/views/3d-planner/hipSpine/interaction/interactionCursors';
import { HipSpineLandmarkName } from '@/domain/anatomy/hipSpineLandmarks';
import { worldPerScreenPx } from '@/lib/threeScene/cameras/orthoUnits';
import { createPlaneProjection } from '@/lib/threeScene/interaction/createPlaneProjection';
import {
    createPointerDragController,
    type ProjectedPointer,
} from '@/lib/threeScene/interaction/createPointerDragController';
import {
    setAnnotationDragOpacity,
    type DragOpacityTarget,
} from '@/views/3d-planner/hipSpine/scene/landmarkSceneSync';
import { landmarkToImagePoint } from '@/views/3d-planner/hipSpine/hipSpineCoordinates';

// Target tolerance in CSS pixels for detecting hits on the femur head dashed
// circle edge. Converted to world units at the call site using `camera.zoom`.
const RING_HIT_TOLERANCE_SCREEN_PX = 6;

// Pointer must move this many CSS pixels from pointerdown before a click is
// promoted to a drag. Bare clicks should not trigger a drag/rebuild.
const DRAG_PROMOTION_THRESHOLD_PX = 3;

// Opacity for the dragged annotation so the X-ray shows beneath it.
const DRAG_OPACITY = 0.3;

type Editor = ReturnType<typeof useHipSpineXRayEditViewModel>;
type Store = ReturnType<typeof useHipSpineStore>;

type Hit =
    | { kind: 'landmark'; name: string; postureId: string }
    | { kind: 'circle-edge'; head: 1 | 2; postureId: string };

export type LandmarkInteractionOptions = {
    canvas: HTMLCanvasElement;
    camera: OrthographicCamera;
    sceneContext: HipSpineXraySceneContext;
    editVm: Editor;
    store: Store;
    /** When true, suppresses drag and hover — used to lock out interaction during a save. */
    isLocked?: () => boolean;
};

/** Shared read-only context threaded through the hit-detection helpers. */
type HitContext = {
    store: Store;
    editVm: Editor;
    camera: OrthographicCamera;
    canvas: HTMLCanvasElement;
    sceneContext: HipSpineXraySceneContext;
    raycaster: Raycaster;
};

function getLandmarkObjects(ctx: HitContext): Object3D[] {
    const postureId = ctx.store.activePostureId;
    if (!postureId) return [];
    const viewState = ctx.store.viewState(postureId);
    if (!viewState) return [];
    const out: Object3D[] = [];
    // Look up by node id via the SceneContext's object map. Avoids the
    // cross-tree fragility of scene.getObjectByName, which could collide
    // with same-named objects elsewhere in the scene graph.
    for (const group of viewState.annotationGroups) {
        for (const child of group.children) {
            if (typeof child.id !== 'string' || !child.id.startsWith('landmark-')) continue;
            const obj = ctx.sceneContext.objectMap.get(child.id);
            if (obj) out.push(obj);
        }
    }
    return out;
}

function findHit(world: Vector2, ctx: HitContext): Hit | null {
    const postureId = ctx.store.activePostureId;
    if (!postureId) return null;
    const intersects = ctx.raycaster.intersectObjects(getLandmarkObjects(ctx), false);
    if (intersects.length > 0) {
        return { kind: 'landmark', name: intersects[0].object.name, postureId };
    }
    const viewState = ctx.store.viewState(postureId);
    if (!viewState?.imageSize) return null;
    const resolved = ctx.editVm.resolvedLandmarks(postureId);
    const tolerance =
        RING_HIT_TOLERANCE_SCREEN_PX * worldPerScreenPx(ctx.camera, ctx.canvas.clientHeight);
    for (const head of [1, 2] as const) {
        const hjcName = head === 1 ? HipSpineLandmarkName.HJC1 : HipSpineLandmarkName.HJC2;
        const lm = resolved.find((l) => l.name === hjcName);
        if (!lm) continue;
        const center = landmarkToImagePoint(lm, viewState.imageSize);
        // resolvedFemurRadius is a width-normalized fraction; scale to pixels for the hit-test
        // (center and tolerance are both in scene pixels).
        const radiusPx =
            ctx.editVm.resolvedFemurRadius(postureId, head) * viewState.imageSize.width;
        if (isPointOnCircleEdge(world, center, radiusPx, tolerance)) {
            return { kind: 'circle-edge', head, postureId };
        }
    }
    return null;
}

function applyHoverState(
    hit: Hit | null | undefined,
    canvas: HTMLCanvasElement,
    editVm: Editor,
): void {
    if (hit?.kind === 'landmark') {
        canvas.style.cursor = INTERACTION_CURSORS.hoverLandmark;
        editVm.hover = { kind: 'landmark', landmarkName: hit.name };
    } else if (hit?.kind === 'circle-edge') {
        canvas.style.cursor = INTERACTION_CURSORS.hoverCircleEdge;
        editVm.hover = { kind: 'circle-edge', head: hit.head };
    } else {
        canvas.style.cursor = INTERACTION_CURSORS.default;
        editVm.hover = { kind: 'none' };
    }
}

function applyDragMove(hit: Hit, world: Vector2, ctx: HitContext, isLocked: () => boolean): void {
    const { store, editVm } = ctx;
    // Defensive backstop: the sync engine defers saves until pointer-up (it never saves
    // while a drag is active), so a save should not lock mid-drag. If that invariant ever
    // breaks and a save does begin during a drag, abort the drag so no further coordinates
    // are written into an overlay that clearPostureEdits is about to wipe. isLocked is the
    // primary gate at drag *start*/hover in pointerProjection.
    if (isLocked()) {
        if (editVm.dragState.kind !== 'idle') editVm.endDrag();
        return;
    }
    // Bail if the active posture has flipped between rAF schedule and fire;
    // syncing against the new posture's groups would corrupt the scene.
    if (hit.postureId !== store.activePostureId) return;
    const drag = editVm.dragState;
    if (drag.kind === 'idle') return;
    if (drag.kind === 'landmark') {
        editVm.updateLandmarkDrag(world);
        editVm.syncSceneToEdits(hit.postureId);
    } else if (drag.kind === 'femur-resize') {
        editVm.updateFemurHeadRadiusResize(world);
        // Femur-resize only changes the radius — skip the connecting-line
        // rebuild that syncSceneToEdits would do for nothing.
        editVm.syncFemurCircleToScene(hit.postureId);
    }
}

type AnnotationDimmer = { dim: (t: DragOpacityTarget) => void; restore: () => void };

function createAnnotationDimmer(store: Store): AnnotationDimmer {
    let dimmed: DragOpacityTarget | null = null;
    const groups = () => store.viewState(store.activePostureId ?? '')?.annotationGroups;
    return {
        dim(target: DragOpacityTarget) {
            const g = groups();
            if (!g) return;
            setAnnotationDragOpacity(g, target, DRAG_OPACITY);
            dimmed = target;
        },
        restore() {
            if (!dimmed) return;
            const g = groups();
            if (g) setAnnotationDragOpacity(g, dimmed, 1);
            dimmed = null;
        },
    };
}

/** Wires camera-control gating and defensive end-drag; returns a combined stop handle. */
function setupControlGating(
    editVm: Editor,
    store: Store,
    sceneContext: HipSpineXraySceneContext,
): StopHandle {
    // flush: 'sync' so control gating and the loupe flag land with the drag-state
    // transition, without a one-frame flicker. The cursor is deliberately NOT driven
    // from here: dragState only leaves 'idle' past the promotion threshold, so it
    // would stay open-handed while the button is already held.
    const stopWatchDrag = watch(
        () => editVm.dragState.kind,
        (kind) => {
            sceneContext.state.rendering.camera.enableControls = kind === 'idle';
            editVm.loupe.active = kind !== 'idle';
        },
        { immediate: true, flush: 'sync' },
    );
    const stopWatchPanel = watch(
        () => [store.activePanelView, store.activePostureId],
        () => {
            if (editVm.dragState.kind !== 'idle') editVm.endDrag();
        },
    );
    return stopAll(stopWatchDrag, stopWatchPanel);
}

/**
 * Set up pointer-event-based landmark editing on the X-ray canvas.
 * Returns a StopHandle that removes all listeners and resets cursor state.
 *
 * Calls `editVm` actions to mutate state, then `editVm.syncSceneToEdits` to push
 * changes into existing scene nodes. Camera-control gating is driven by a Pinia
 * watcher on `editVm.dragState` so any external state change (e.g. a defensive
 * end-drag) flips controls back on without going through this controller.
 * Control gating writes to `state.rendering.camera.enableControls` directly so
 * `updateCamera` propagates the value reactively.
 */
export function setupLandmarkInteraction(options: LandmarkInteractionOptions): StopHandle {
    const { canvas, camera, sceneContext, editVm, store, isLocked = () => false } = options;
    const projection = createPlaneProjection(canvas, camera);
    const hitCtx: HitContext = {
        store,
        editVm,
        camera,
        canvas,
        sceneContext,
        raycaster: projection.raycaster,
    };
    const dimmer = createAnnotationDimmer(store);

    const pointerProjection = (event: PointerEvent): ProjectedPointer<Hit> => {
        if (!editVm.isEditMode || isLocked()) return { hit: null, world: new Vector2() };
        const world = projection.project(event);
        return { hit: findHit(world, hitCtx), world };
    };

    const stopController = createPointerDragController<Hit>({
        canvas,
        pointerProjection,
        dragThresholdPx: DRAG_PROMOTION_THRESHOLD_PX,
        // Promotion still needs the full travel threshold so a bare click cannot move
        // a landmark; the cursor must not wait for it.
        onPress: () => {
            canvas.style.cursor = INTERACTION_CURSORS.dragging;
        },
        // Re-read the target rather than reusing the pressed hit: a clamped drag
        // leaves it behind, and a cancelled or locked press has none.
        onPressEnd: (_pressed, release) => applyHoverState(release?.hit, canvas, editVm),
        onDragStart: (hit, grabWorld) => {
            if (hit.kind === 'landmark') {
                editVm.beginLandmarkDrag(hit.postureId, hit.name, grabWorld);
                dimmer.dim({ kind: 'landmark', name: hit.name });
            } else {
                editVm.beginFemurHeadRadiusResize(hit.postureId, hit.head, grabWorld);
                dimmer.dim({ kind: 'femur', head: hit.head });
            }
        },
        onDrag: (hit, world) => applyDragMove(hit, world, hitCtx, isLocked),
        onDragEnd: () => {
            dimmer.restore();
            if (editVm.dragState.kind !== 'idle') editVm.endDrag();
        },
        onHover: (hit) => applyHoverState(hit, canvas, editVm),
        onPointerLeave: () => {
            editVm.hover = { kind: 'none' };
            canvas.style.cursor = INTERACTION_CURSORS.default;
        },
    });

    const stopGating = setupControlGating(editVm, store, sceneContext);

    return stopAll(stopController, stopGating, () => {
        dimmer.restore();
        // Reset Pinia drag state — the store survives modal close, and a stale
        // non-idle dragState would lock out the camera controls on reopen.
        if (editVm.dragState.kind !== 'idle') editVm.endDrag();
        canvas.style.cursor = INTERACTION_CURSORS.default;
    });
}
